import { getConditionalFilters } from "./getConditionalFilters";
import {
  addFilterValue,
  assembleFilterStringsFromArray,
  renumberFiltersQueryString,
  setFilterString,
  setIgnoreCaseSensitivityString,
  setParameterizedStringWithValue,
  setSubQueryString,
} from "./queryBuilder";
import { isEmpty, isObject, pick } from "lodash-es";
import { stringifyMinimal } from "../../../utils/browser";
import { FILTER_TYPE_DATE_PRESETS } from "../../../utils/constants/constants";
import { xDaysFuncs } from "../dataSettings/dataSettings";
import {
  CacheableMenuFilterValue,
  MenuFilterState,
  MenuFilterType,
  MenuFilterValue,
} from "../../../types/menuFilters";

function esc(value: any) {
  return encodeURIComponent(value);
}

export default function (
  query,
  menuFilters: MenuFilterState[],
  activeTab,
  paramValue,
  comparisonMode,
  term
) {
  const {
    isParameterized,
    parameterizedFilterPrefix,
    filters,
    havings = [],
    ignoreMenuFilters,
    filterFromTo,
    ignoreDateTerm,
    inGlobalOverride,
    dateKeys,
  } = query;
  const filtersArray: FiltersArrayItem[] = [];

  /** Menu filters with common "filters" applied */
  const commonFilteredMenuFilters = menuFilters
    .filter(ignoreHiddenFilters)
    .filter(respectWhitelistedFilters);

  const cleanedMenuFilters: MenuFilterState[] = commonFilteredMenuFilters
    .filter(removeMatchingDrilldownFilter)
    .filter(removeBooleanCheckboxFilter)
    .map(convertDatePresetsToDateFilters)
    .map(mapValueTypesIfParameterized);

  const activeGf: MenuFilterState[] =
    cleanedMenuFilters.map(deDupeFromOverrides);

  const numberFilters: MenuFilterState[] = commonFilteredMenuFilters
    .filter((mf) => mf.selectedValue !== undefined)
    .map((mf) => {
      const values = [
        {
          checked: true,
          label: mf.name,
          value: mf.selectedValue!,
          key: mf.name,
          type: (parameterizedFilterPrefix || "") + mf.name,
        },
      ].filter((f) => !ignoreMenuFilters?.includes(f.type));
      return { ...mf, values };
    });

  function isDatePresets(type) {
    return type === FILTER_TYPE_DATE_PRESETS;
  }

  const keys = numberFilters
    .map((v) => v.values)
    .flat()
    .map((item) => item.key); // get slider filters keys

  const gf = [
    ...activeGf.map((menuFilter) => {
      const array = menuFilter.values;
      const values = array.filter((item) => !keys.includes(item.key));
      return { ...menuFilter, values };
    }), // remove slider duplicate
    ...numberFilters,
  ];

  function convertDatePresetsToDateFilters(
    item: MenuFilterState
  ): MenuFilterState {
    if (isDatePresets(item.type) && isParameterized) {
      const { start, end } = dateKeys ?? {};

      const [from, to] = item.values;
      return {
        ...item,
        values: [
          { ...from, key: start + item.name, type: start },
          { ...to, key: end + item.name, type: end },
        ],
      };
    }

    return item;
  }

  // we show boolean filter like a checkbox so user can select both values it that case we remove this filter
  function removeBooleanCheckboxFilter(mf) {
    if (mf.type === "checkboxBoolean") {
      return mf.values.filter((v) => v.checked).length === 1;
    }
    return true;
  }

  // override menu filters which match with drilldown selected row
  function removeMatchingDrilldownFilter(mf) {
    return !filters
      .filter((filter) => filter.isDrilldownFilter)
      .reduce((acc, curr) => {
        const name = curr.type.replace(parameterizedFilterPrefix, "");
        acc = name === mf.name;

        return acc;
      }, false);
  }

  function ignoreHiddenFilters(mf) {
    if (!activeTab || isEmpty(activeTab)) {
      return false;
    }
    return !mf.hide?.some((uuid) => activeTab?.uuid === uuid);
  }

  function respectWhitelistedFilters(mf) {
    if (!activeTab || isEmpty(activeTab)) {
      return false;
    }

    return mf.show?.length > 0
      ? !!mf.show.some((uuid) => activeTab?.uuid === uuid)
      : true;
  }

  function deDupeFromOverrides(item: MenuFilterState): MenuFilterState {
    const values = item.values
      .filter((f) => f.checked)
      .filter(removeIfInGlobalOverride)
      .filter((f) => !ignoreMenuFilters?.includes(f.type));

    return { ...item, values };
  }

  function removeIfInGlobalOverride(f) {
    if (!inGlobalOverride) {
      return true;
    }

    return !filters || (filters && !filters.find((qf) => qf.type === f.type));
  }

  function mapValueTypesIfParameterized(mf: MenuFilterState): MenuFilterState {
    return {
      ...mf,
      values: mf.values.map((v) => ({
        ...v,
        type: setTypeIfParameterized(v, mf.type),
      })),
    };
  }

  function setTypeIfParameterized(v, type) {
    if (isDatePresets(type) && isParameterized) {
      return v.key;
    }

    return isParameterized ? (parameterizedFilterPrefix || "") + v.key : v.key;
  }

  // If there are no global filters assigned
  const globalFilters = gf.filter((item) => item.values.length);

  function filterConditional(array: AppliableFilter[]) {
    return array.filter((f) => f.conditional);
  }

  function filterNonConditional(array: AppliableFilter[]) {
    return array.filter((f) => !f.conditional);
  }

  function mapRestritedValues(field) {
    return {
      ...field,
      value:
        field?.value instanceof String
          ? field.value.replace("$restrict$", paramValue)
          : field?.value,
    };
  }

  const queryFilters: AppliableFilter[] = filters || [];

  const wrapLastXDays = queryFilters.flatMap((curr) => {
    if (xDaysFuncs[curr.operator]) {
      const { start, end } = xDaysFuncs[curr.operator](curr.value);
      return [
        { type: curr.type, value: start },
        { type: curr.type, value: end },
      ];
    }

    return curr;
  });

  const [conditionalFilters, commonFilters] = [
    filterConditional(wrapLastXDays),
    filterNonConditional(wrapLastXDays).map(mapRestritedValues),
  ];

  const qFilters: AppliableFilter[] = conditionalFilters.length
    ? [...getConditionalFilters(conditionalFilters), ...commonFilters].flat()
    : wrapLastXDays;

  if (isParameterized) {
    const combinedActiveQueryAndGlobalFilters: AppliableFilter[][] = [
      globalFilters.map((v) => v.values).flat(),
      qFilters,
    ];
    const activeFiltersByKey = assembleFiltersByKey(
      combinedActiveQueryAndGlobalFilters.flat()
    );

    const dateTerm = ignoreDateTerm ? null : term;

    return setParameterizedStringWithValue(
      activeFiltersByKey,
      filterFromTo,
      menuFilters,
      paramValue,
      comparisonMode,
      dateTerm,
      activeTab?.uuid
    );
  }

  const unhandledGlobalFilters = globalFilters.filter((menuFilter) => {
    const equalOperatorMenuFilterTypes: MenuFilterType[] = [
      "checkbox",
      "checkboxBoolean",
      "multiList",
      "radio",
      "singleList",
    ];
    if (!equalOperatorMenuFilterTypes.includes(menuFilter.type)) {
      return true;
    }

    const values = menuFilter.values
      .map((v) => v.value)
      .filter((v) => !isObject(v));

    if (!menuFilter.additionalDataSourceFields?.length) {
      filtersArray.push({
        name: menuFilter.name,
        operator: "==",
        values,
      });

      return false;
    }

    filtersArray.push({
      groupType: "OR",
      filters: [
        { uuid: menuFilter.dataSourceFieldUuid, name: menuFilter.name },
        ...menuFilter.additionalDataSourceFields,
      ].map((v) => ({
        name: v.name,
        operator: "==",
        values,
      })),
    });

    return false;
  });

  const combinedActiveQueryAndGlobalFilters: AppliableFilter[][] = [
    unhandledGlobalFilters.map((v) => v.values).flat(),
    qFilters,
  ];

  let filtersString = "";
  for (const filters of combinedActiveQueryAndGlobalFilters) {
    filtersString += assembleFilters(filtersArray, filters, filterFromTo);
  }

  filtersString =
    assembleFilterStringsFromArray(filtersArray) +
    renumberFiltersQueryString(filtersString, filtersArray.length) +
    setHavings();

  return filtersString;

  function setHavings() {
    if (!havings.length) {
      return "";
    }

    return `&${stringifyMinimal({
      havings: havings.map((having) =>
        pick(having, ["name", "operator", "values"])
      ),
    })}`;
  }
}

function assembleFiltersByKey(
  filters: AppliableFilter[],
  complexFiltersByKey: Record<string, AppliableFilter[]> = {}
) {
  return filters.reduce((acc, curr) => {
    if (complexFiltersByKey[curr.type]) {
      // Let's handle complex filters separately.
      complexFiltersByKey[curr.type].push(curr);
      return acc;
    }

    const values =
      // case when dynamicFilters (drilldown) filter name match with filters subQuery from config
      // this can happen when user do not set filters in drilldown level directly and inheritance of configs move them to inner level
      acc[curr.type] && Array.isArray(acc[curr.type])
        ? [...acc[curr.type], esc(curr.value)]
        : [esc(curr.value)];
    // Remove duplicate operators because the API does not accept an array of operators.
    if (curr.operator && !values.includes(curr.operator)) {
      values.push(curr.operator);
    }

    const filterObj: any = { ...acc, [curr.type]: values };

    //  need when operators are used on blended query
    if (curr.ds) {
      values.push("ds:" + curr.ds);
    }

    // stackingType need for filters condition (OR, AND)
    if (curr.stackingType) {
      filterObj.stackingType = [curr.stackingType];
    }

    if (curr.aggregated) {
      values.push("aggregated");
    }

    // subQueryValues support
    if (curr.subQueryValues) {
      filterObj[curr.type] = { ...curr.subQueryValues, hasSubQuery: true };
    }

    if (curr.ignoreCaseSensitivity) {
      filterObj[`ignoreCaseSensitivity:${curr.type}`] = 1;
    }

    return filterObj;
  }, {});
}

function assembleFilters(
  filtersArray: FiltersArrayItem[],
  queryFiltersOrGlobalMenuFilters: AppliableFilter[],
  filterFromTo
) {
  // Filters e.g. ranges, where we'll need an OR filterGroup rather than sending the filter as a simple array of values.
  const complexFiltersByKey: Record<string, AppliableFilter[]> = {};

  for (const filter of queryFiltersOrGlobalMenuFilters) {
    if (filter.value && typeof filter.value === "object") {
      complexFiltersByKey[filter.type] = [];
    }
  }

  const activeFiltersByKey: Record<string, RegularFilter> =
    assembleFiltersByKey(queryFiltersOrGlobalMenuFilters, complexFiltersByKey);

  handleComplexFilters();

  return setRegularFilters();

  function getValueStrings(arr: MenuFilterValue[]) {
    return arr.reduce((a, c) => a + addFilterValue(c, null), "");
  }

  function setRegularFilters() {
    return Object.entries(activeFiltersByKey).reduce((acc, curr) => {
      // if has subQueryValues then handle payload different
      if (
        (typeof curr[1] === "object" || Array.isArray(curr[1])) &&
        "hasSubQuery" in curr[1] &&
        curr[1].hasSubQuery
      ) {
        const setSubQuery = setSubQueryString(curr[0], curr[1]);
        return acc + setSubQuery;
      }

      if (curr[0] === "ds") {
        return acc + getValueStrings(curr[1]);
      }

      if (curr[0].startsWith("ignoreCaseSensitivity:")) {
        return acc + setIgnoreCaseSensitivityString();
      }

      const current =
        filterFromTo?.from === curr[0] ? filterFromTo.to : curr[0];
      const setName = setFilterString(current);

      return acc + setName + getValueStrings(curr[1]);
    }, "");
  }

  // E.g. ranges.
  function handleComplexFilters() {
    const preparedFilters: OuterOuterFilter[] = [];

    for (const filters of Object.values(complexFiltersByKey)) {
      const outerFilters: OuterFilter[] = [];

      for (const filter of filters) {
        const value = filter.value;
        if (value && typeof value === "object") {
          // Handle ranges.
          const innerFilters: InnerFilter[] = [];

          if (value.min != null) {
            innerFilters.push({
              name: filter.type,
              values: [value.min],
              conjunction: filter.conjunction,
              operator: ">=",
            });
          }

          if (value.max != null) {
            innerFilters.push({
              name: filter.type,
              values: [value.max],
              conjunction: filter.conjunction,
              operator: "<=",
            });
          }

          if (innerFilters.length) {
            outerFilters.push({
              groupType: "AND",
              filters: innerFilters,
            });
          }
        } else {
          const outerFilter: OuterFilter = {
            name: filter.type,
            values: [value],
          };
          if (filter.operator) {
            outerFilter.operator = filter.operator;
          }
          if (filter.alias) {
            outerFilter.alias = filter.alias;
          }
          outerFilters.push(outerFilter);
        }
      }
      preparedFilters.push({
        groupType: "OR",
        filters: outerFilters,
      });
    }
    filtersArray.push(...convertFiltersByConjunction(preparedFilters));
  }
}

/**
 * Converts a list of filters by grouping those that contain "OR" conjunctions.
 * If no conjunctions are found, the original filters are returned.
 *
 * @param {Array} filters - The array of filter objects to process.
 * @returns {Array} - The modified array of filters, with "OR" conjunctions processed.
 */
function convertFiltersByConjunction(filters: OuterOuterFilter[]) {
  const conjunctions = filters.filter(hasOrConjunction);
  const regularRanges = filters.filter(hasRegularConjunction);

  if (conjunctions.length === 0) {
    return filters;
  }

  // Combine the filters from conjunctions into a single object
  const converted = conjunctions.reduce(
    (acc, curr) => {
      acc.groupType = curr.groupType;
      acc.filters.push(...curr.filters);
      return acc;
    },
    { filters: [], groupType: "OR" }
  );

  return [...regularRanges, converted];
}

/**
 * Checks if the given filter item contains any nested filters with "OR" conjunctions.
 *
 * @param {Object} item - The filter object to check.
 * @returns {boolean} - True if any nested filters contain "OR", otherwise false.
 */
function hasOrConjunction(item) {
  return item.filters.some((nestedItem) =>
    nestedItem.filters.some((subItem) => subItem.conjunction === "OR")
  );
}

/**
 * Checks if the given filter item contains any nested filters with conjunctions other than "OR".
 *
 * @param {Object} item - The filter object to check.
 * @returns {boolean} - True if any nested filters contain conjunctions other than "OR", otherwise false.
 */
function hasRegularConjunction(item) {
  return item.filters.some((nestedItem) =>
    nestedItem.filters.some((subItem) => subItem.conjunction !== "OR")
  );
}

type AppliableFilter = {
  type: string;
  value: MenuFilterValue;
  operator?: string;
  ds?: string;
  alias?: string;
  stackingType?: string;
  aggregated?: boolean;
  subQueryValues?: SubQueryValue;
  conjunction?: "AND" | "OR";
  conditional?: boolean;
  ignoreCaseSensitivity?: "1";
};

type SubQueryValue = {
  type: string;
  operator?: string;
  value: MenuFilterValue;
  aggregation?: string;
};

type RegularFilter =
  | (MenuFilterValue[] & { hasSubQuery: never })
  | (SubQueryValue & {
      hasSubQuery: true;
    });

type InnerFilter = {
  name: string;
  values: CacheableMenuFilterValue[];
  conjunction?: "AND" | "OR";
  operator: string;
};

type OuterFilter =
  | {
      name: string;
      values: CacheableMenuFilterValue[];
      operator?: string;
      alias?: string;
    }
  | GroupedFilter<InnerFilter>;

type OuterOuterFilter = GroupedFilter<OuterFilter>;

/**
 * This type was made generic, because there's only limited depth
 * supported for grouped filters.
 */
type GroupedFilter<T> = {
  groupType: "AND" | "OR";
  filters: T[];
};

type FiltersArrayItem = OuterOuterFilter | InnerFilter;
